import { Component, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, SelectionChangedEvent } from 'ag-grid-community';

import { CommonService } from '../../services/common.service';
import { ReportsService } from '../../services/reports.service';
import { CasualReportService, CasualDialogTypesEnum } from '../../services/casual-report.service';
import { ReportOption, OtherMiniReportOptions } from 'src/app/model/reports';

import { TitleLinkButtonCasualReportComponent } from '../ag-grid-cell-renderers/title-link-button-casual-report.component';
import { FileExtensionAsIconComponent } from '../ag-grid-cell-renderers/file-extension-as-icon.component';
import { ActionOptionButtonCasualReportComponent } from '../ag-grid-cell-renderers/action-option-button-casual-report.component';
import { SendFileActionButtonComponent } from '../ag-grid-cell-renderers/send-file-action-button-casual-report.component';
import { OpenResourceInNewTabOrWindowComponent } from '../ag-grid-cell-renderers/open-resource-in-new-tab-win.component';
import { ICustomAgGridComponents } from 'src/app/model/ag-grid-custom-models/custom-components-config';

@Component({
  selector: 'vrm-casual-report',
  templateUrl: './vrm-casual-report.component.html',
  styleUrls: ['./vrm-casual-report.component.scss'],
})
export class VrmCasualReportComponent {
  @Input() metadata: any;
  @Input() rawData: any;
  @Input() reportTitle: string;
  @Input() reportOptions: ReportOption;
  @Input() otherOptions?: OtherMiniReportOptions;

  @Output() showDialog: EventEmitter<any> = new EventEmitter();

  agMobileColumns: ColDef[] = [];

  columnsToDisplay: ColDef[];
  agGridPcColumns: ColDef[];

  doubleClickedRowData: any;

  initialLoad: boolean = true;
  initialView: string;

  customComponents: ICustomAgGridComponents;
  statusBar: any;
  gridOptions: any;
  isRtlLanguage: boolean;

  @ViewChild('agGridCasualReport', { static: true }) agGrid: AgGridAngular;

  constructor(
    private cService: CommonService,
    private rptService: ReportsService,
    private crService: CasualReportService
  ) {
    // TODO: Looks like isRtl needs to be watched. Use RxJs!
    this.isRtlLanguage =
      cService.selectedLanguage && cService.selectedLanguage.isRtl ? cService.selectedLanguage.isRtl : false;
    setTimeout(() => {
      if (this.otherOptions.hasHeader) {
        this.cService.setPageHeader(this.reportTitle, true);
      }
    });

    this.initializeAgGridProperties();
    this.initialView = cService.isPcViewport ? 'pc' : 'mobile';
  }

  /* AG-GRID LIFECYCLE HOOKS
    ------------------------ */
  initializeAgGridProperties() {
    // Used to specify Ag-Grid specific sub components
    this.customComponents = {
      titleLinkButtonCasualReportComponent: TitleLinkButtonCasualReportComponent,
      fileExtensionAsIconComponent: FileExtensionAsIconComponent,
      actionOptionButtonCasualReportComponent: ActionOptionButtonCasualReportComponent,
      sendFileActionButtonComponent: SendFileActionButtonComponent,
      openResourceInNewTabOrWindowComponent: OpenResourceInNewTabOrWindowComponent,
    };

    this.statusBar = {
      statusPanels: this.crService.agGridConfig.statusPanels,
    };

    // Should come last mostly
    this.gridOptions = {
      defaultColDef: this.crService.agGridConfig.defaultColumnConfig,
      suppressRowClickSelection: false,
      statusBar: this.statusBar,
      rowSelection: 'single',
      ...this.rptService.agGridConfig.common,
    };
  }

  agGridReady() {
    this.displayColumnsByViewportSize();

    setTimeout(() => {
      this.initialLoad = false;
    }, 500);
  }

  /*  EVENTS
    -------------- */
  cellDoubleClicked(event: { data: any; colDef: ColDef }) {
    this.doubleClickedRowData = event.data;

    if (this.doubleClickedRowData.url) {
      window.open(this.doubleClickedRowData.url, '_blank');
    } else {
      this.activateDialog();
    }

    console.log('%c Cell double clicked', 'color: limegreen', this.doubleClickedRowData);
  }

  getRowSelection(event: SelectionChangedEvent) {
    this.crService.selectedRows = event.api.getSelectedRows();
    console.log('%c Selected Casual Report Row', 'color: salmon', this.crService.selectedRows);

    // Used when an Intervention is opened in a new Window / Tab
    localStorage.setItem(
      this.cService.localStorageKeys.selectedCasualReportRow,
      JSON.stringify(this.crService.selectedRows[0])
    );
  }

  gridSortChanged() {
    this.crService.gridState.sortModel = this.agGrid.columnApi.getColumnState();
    console.log('%c Sort Model', 'color: orange', this.crService.gridState.sortModel);

    // Show or hide back-end's default sort indicator
    setTimeout(() => {
      const dummySortIndicators = document.querySelectorAll('.dummy-sort-indicator');
      if (dummySortIndicators) {
        if (this.rptService.gridState.sortModel && this.rptService.gridState.sortModel.length > 0) {
          dummySortIndicators.forEach((dummySortIndicator: Element) => {
            dummySortIndicator.classList.add('ag-hidden');
          });
        } else {
          dummySortIndicators.forEach((dummySortIndicator: Element) => {
            dummySortIndicator.classList.remove('ag-hidden');
          });
        }
      }
    });
  }

  /* METHODS
    ----------- */
  private displayColumnsByViewportSize() {
    if (this.agGrid.columnApi.isPivotMode) {
      this.agGrid.columnApi.setPivotMode(false);
    }

    if (this.metadata && this.metadata.structure) {
      this.agGridPcColumns = this.crService.convertToAgColumnDefinition(this.metadata.structure, this.metadata.name);
    } else {
      this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.formatNotSupported);
      console.warn('VRM WARNING: Invalid Casual Report Structure');
    }

    this.columnsToDisplay = this.agGridPcColumns;
    this.commonGridConfig();
  }

  private commonGridConfig() {
    // Run Async - Ensure grid is attached to the DOM
    setTimeout(() => {
      if (this.columnsToDisplay.length < 10 && !this.cService.isMobileViewport && !this.metadata.groupStructure) {
        this.agGrid.api.sizeColumnsToFit();
      } else {
        this.agGrid.columnApi.autoSizeAllColumns();
      }
    });
  }

  activateDialog() {
    if (this.doubleClickedRowData) {
      this.showDialog.emit({
        maxHeight: this.cService.isMobileViewport ? '100%' : '',
        maxWidth: this.cService.isMobileViewport ? '100%' : '',
        height: '100%',
        width: this.cService.isMobileViewport ? '100%' : '95%',

        data: {
          selectedRow: this.doubleClickedRowData,
          casualDialogType: CasualDialogTypesEnum.guideViewer,
        },

        disableClose: true,
      });
    } else {
      this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.failedToProcessData);
      console.error('VRM ERROR: Cannot drilldown without valid data selection');
    }
  }
}
